import React, { useEffect, useMemo, useState } from 'react'
import API from '../../services/API'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  Button,
} from '@mui/material'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import InboxRoundedIcon from '@mui/icons-material/InboxRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import SelectedFeedback from './SelectedFeedback'
import {
  TYPE_CONFIGS,
  TYPE_ORDER,
  formatTicketDate as formatDate,
  getPriorityConfig,
  getStatusConfig,
  getTypeKey,
} from './supportTicketMeta'

// Rows shown per list before "Show all" — keeps long histories scannable.
const PAGE_SIZE = 8

// Only raised priorities are worth surfacing in the list; Normal/Low is noise.
const getRaisedPriority = (priority = '') => {
  const config = getPriorityConfig(priority)
  return ['Urgent', 'High'].includes(config.label) ? config : null
}

const sortByDate = (tasks = []) => [...tasks].sort((a, b) => b.date - a.date)

const matchesFilters = (task, { query, typeKey }) => {
  if (typeKey !== 'all' && getTypeKey(task.type) !== typeKey) return false
  if (!query) return true
  return [task.name, task.ticketRef, task.description]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(query))
}

export default function MyFeedback() {
  const [openTasks, setOpenTasks] = useState([])
  const [selectedFeedback, setSelectedFeedback] = useState(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [typeKey, setTypeKey] = useState('all')

  // Closed tasks are only fetched from ClickUp once the user expands the
  // "Completed" accordion, so the initial load stays light.
  const [completedExpanded, setCompletedExpanded] = useState(false)
  const [completedTasks, setCompletedTasks] = useState(null) // null = not fetched yet
  const [completedLoading, setCompletedLoading] = useState(false)
  const [completedError, setCompletedError] = useState(false)

  useEffect(() => {
    API.get('/dev/userrequests', { params: { scope: 'open' } })
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setOpenTasks(sortByDate(res.data))
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }, [])

  const loadCompleted = () => {
    setCompletedLoading(true)
    setCompletedError(false)
    API.get('/dev/userrequests', { params: { scope: 'closed' } })
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setCompletedTasks(sortByDate(res.data))
        } else {
          setCompletedError(true)
        }
      })
      .catch((e) => {
        console.log(e)
        setCompletedError(true)
      })
      .finally(() => setCompletedLoading(false))
  }

  const handleCompletedToggle = (_event, expanded) => {
    setCompletedExpanded(expanded)
    if (expanded && completedTasks === null && !completedLoading) {
      loadCompleted()
    }
  }

  const filters = useMemo(
    () => ({ query: search.trim().toLowerCase(), typeKey }),
    [search, typeKey],
  )
  const filtering = Boolean(filters.query) || typeKey !== 'all'

  const visibleOpen = useMemo(
    () => openTasks.filter((task) => matchesFilters(task, filters)),
    [openTasks, filters],
  )
  const visibleCompleted = useMemo(
    () =>
      (completedTasks || []).filter((task) => matchesFilters(task, filters)),
    [completedTasks, filters],
  )

  // Type filter chips only list types the user actually has tickets of.
  const presentTypes = useMemo(() => {
    const keys = new Set(
      [...openTasks, ...(completedTasks || [])].map((t) => getTypeKey(t.type)),
    )
    return TYPE_ORDER.filter((key) => keys.has(key))
  }, [openTasks, completedTasks])

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 4,
          border: '1px solid rgba(15,23,42,0.08)',
          background: 'rgba(255,255,255,0.78)',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={22} />
          <Box>
            <Typography fontWeight={900}>Loading your requests</Typography>
            <Typography variant="body2" color="text.secondary">
              Fetching the latest updates from ClickUp.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    )
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography fontWeight={950}>Active requests</Typography>
          <Typography variant="caption" color="text.secondary">
            {openTasks.length
              ? `${openTasks.length} currently open`
              : 'Nothing open right now'}
          </Typography>
        </Box>
        <TextField
          size="small"
          placeholder="Search by title or ticket ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: '100%', sm: 260 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
            sx: { borderRadius: 2.5, bgcolor: '#fff' },
          }}
        />
      </Stack>

      {presentTypes.length > 1 && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <FilterChip
            label="All"
            active={typeKey === 'all'}
            onClick={() => setTypeKey('all')}
          />
          {presentTypes.map((key) => (
            <FilterChip
              key={key}
              label={TYPE_CONFIGS[key].label}
              color={TYPE_CONFIGS[key].color}
              active={typeKey === key}
              onClick={() => setTypeKey(typeKey === key ? 'all' : key)}
            />
          ))}
        </Stack>
      )}

      {openTasks.length ? (
        visibleOpen.length ? (
          <RequestList
            // Reset "Show all" whenever the filters change.
            key={`${filters.query}|${typeKey}`}
            tasks={visibleOpen}
            onSelect={setSelectedFeedback}
          />
        ) : (
          <NoMatches />
        )
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 4,
            textAlign: 'center',
            border: '1px solid rgba(15,23,42,0.08)',
            background:
              'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(248,250,252,0.9))',
          }}
        >
          <InboxRoundedIcon sx={{ fontSize: 44, color: '#239AA4', mb: 1 }} />
          <Typography variant="h6" fontWeight={950}>
            No active requests
          </Typography>
          <Typography color="text.secondary">
            When you submit a bug, feature request, or support query, it will
            appear here. Previously resolved requests are available under
            Completed below.
          </Typography>
        </Paper>
      )}

      <Accordion
        disableGutters
        elevation={0}
        expanded={completedExpanded}
        onChange={handleCompletedToggle}
        sx={{
          borderRadius: 3,
          border: '1px solid rgba(15,23,42,0.08)',
          background: 'rgba(255,255,255,0.78)',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreRoundedIcon />}
          sx={{ px: 2, minHeight: 56 }}
        >
          <Box>
            <Typography fontWeight={950}>Completed</Typography>
            <Typography variant="caption" color="text.secondary">
              {completedTasks === null
                ? 'Expand to load resolved or closed requests'
                : `${completedTasks.length} resolved or closed`}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pb: 2 }}>
          {completedLoading && (
            <Stack direction="row" spacing={1.5} alignItems="center" py={1}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading completed requests from ClickUp…
              </Typography>
            </Stack>
          )}

          {!completedLoading && completedError && (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              justifyContent="space-between"
              py={1}
            >
              <Typography variant="body2" color="text.secondary">
                We couldn&apos;t load your completed requests.
              </Typography>
              <Button size="small" variant="outlined" onClick={loadCompleted}>
                Retry
              </Button>
            </Stack>
          )}

          {!completedLoading &&
            !completedError &&
            completedTasks !== null &&
            (completedTasks.length ? (
              visibleCompleted.length ? (
                <RequestList
                  key={`${filters.query}|${typeKey}`}
                  tasks={visibleCompleted}
                  onSelect={setSelectedFeedback}
                  muted
                />
              ) : (
                <NoMatches />
              )
            ) : (
              <Typography variant="body2" color="text.secondary" py={1}>
                No completed requests yet.
              </Typography>
            ))}
        </AccordionDetails>
      </Accordion>

      {filtering && (
        <Button
          size="small"
          onClick={() => {
            setSearch('')
            setTypeKey('all')
          }}
          sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
        >
          Clear filters
        </Button>
      )}

      <SelectedFeedback
        selectedFeedback={selectedFeedback}
        setSelectedFeedback={setSelectedFeedback}
      />
    </Stack>
  )
}

function FilterChip({ label, active, onClick, color = '#1A2230' }) {
  return (
    <Chip
      size="small"
      label={label}
      onClick={onClick}
      variant={active ? 'filled' : 'outlined'}
      sx={{
        fontWeight: 800,
        borderColor: 'rgba(15,23,42,0.14)',
        color: active ? '#fff' : color,
        bgcolor: active ? color : 'transparent',
        '&&:hover': { bgcolor: active ? color : 'rgba(15,23,42,0.05)' },
      }}
    />
  )
}

function NoMatches() {
  return (
    <Typography variant="body2" color="text.secondary" py={1}>
      No requests match your search.
    </Typography>
  )
}

function RequestList({ tasks, onSelect, muted = false }) {
  const [showAll, setShowAll] = useState(false)
  const shown = showAll ? tasks : tasks.slice(0, PAGE_SIZE)
  const hidden = tasks.length - shown.length

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid rgba(15,23,42,0.08)',
        bgcolor: '#fff',
      }}
    >
      {shown.map((task, index) => (
        <RequestRow
          key={task.id}
          task={task}
          muted={muted}
          divider={index > 0}
          onClick={() => onSelect(task)}
        />
      ))}
      {hidden > 0 && (
        <ButtonBase
          onClick={() => setShowAll(true)}
          sx={{
            width: '100%',
            py: 1.25,
            borderTop: '1px solid rgba(15,23,42,0.06)',
            fontSize: 13,
            fontWeight: 800,
            color: '#239AA4',
            '&:hover': { bgcolor: 'rgba(35,154,164,0.05)' },
          }}
        >
          Show {hidden} more
        </ButtonBase>
      )}
    </Paper>
  )
}

function RequestRow({ task, onClick, muted, divider }) {
  const status = getStatusConfig(task.status)
  const type = TYPE_CONFIGS[getTypeKey(task.type)]
  const TypeIcon = type.icon
  const priority =
    task.status?.toLowerCase() !== 'closed'
      ? getRaisedPriority(task.priority?.priority)
      : null

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.75,
        py: 1.1,
        textAlign: 'left',
        borderTop: divider ? '1px solid rgba(15,23,42,0.06)' : 'none',
        opacity: muted ? 0.8 : 1,
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: 'rgba(35,154,164,0.05)' },
        '&:hover .request-row-chevron': { color: '#239AA4' },
      }}
    >
      <Box
        title={type.label}
        sx={{
          width: 30,
          height: 30,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: type.bg,
          color: type.color,
          flexShrink: 0,
        }}
      >
        <TypeIcon sx={{ fontSize: 17 }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="baseline" minWidth={0}>
          <Typography
            fontWeight={850}
            noWrap
            sx={{ color: '#1A2230', fontSize: 14, lineHeight: 1.3 }}
          >
            {task.name || 'Untitled request'}
          </Typography>
          {priority && (
            <Typography
              variant="caption"
              sx={{ color: priority.color, fontWeight: 900, flexShrink: 0 }}
            >
              {priority.label}
            </Typography>
          )}
        </Stack>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ color: 'text.secondary', minWidth: 0 }}
        >
          {task.ticketRef && (
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 700,
                color: '#475569',
                flexShrink: 0,
              }}
            >
              {task.ticketRef}
            </Typography>
          )}
          {task.ticketRef && <Dot />}
          <Typography variant="caption" sx={{ flexShrink: 0 }}>
            {formatDate(task.date)}
          </Typography>
          {task.description && (
            <>
              <Dot sx={{ display: { xs: 'none', md: 'block' } }} />
              <Typography
                variant="caption"
                noWrap
                sx={{ display: { xs: 'none', md: 'block' }, minWidth: 0 }}
              >
                {task.description}
              </Typography>
            </>
          )}
        </Stack>
      </Box>

      <Chip
        size="small"
        label={status.label}
        sx={{
          height: 22,
          flexShrink: 0,
          fontSize: 11.5,
          fontWeight: 900,
          color: status.color,
          bgcolor: status.bg,
        }}
      />

      <ChevronRightRoundedIcon
        className="request-row-chevron"
        sx={{
          display: { xs: 'none', sm: 'block' },
          color: 'rgba(15,23,42,0.25)',
          flexShrink: 0,
          transition: 'color 0.15s ease',
        }}
      />
    </ButtonBase>
  )
}

function Dot({ sx }) {
  return (
    <Box
      component="span"
      sx={{
        width: 3,
        height: 3,
        borderRadius: '50%',
        bgcolor: 'rgba(15,23,42,0.3)',
        flexShrink: 0,
        ...sx,
      }}
    />
  )
}
